/**
 * Product analytics — opt-in only. Events flow to Rollbar telemetry when
 * the user enables optional storage & diagnostics in Settings or the banner.
 */

import {
  markD1Reported,
  markD7CandidateReported,
  msSinceFirstOpen,
  recordActiveSession,
  recordSignup,
} from "@/lib/activationFunnel";
import { hasOptionalStorageConsent } from "@/lib/privacyConsent";
import {
  captureRollbarEvent,
} from "@/lib/rollbarReport";
import { isRollbarConfiguredForApp } from "@/lib/rollbar";

function rollbarReady(): boolean {
  return isRollbarConfiguredForApp() && hasOptionalStorageConsent();
}

function timeToValuePayload(): Record<string, number | null> {
  const ms = msSinceFirstOpen();
  return {
    time_to_value_ms: ms,
    time_to_value_min: ms !== null ? Math.round(ms / 60_000) : null,
  };
}

export function trackEvent(
  name: string,
  properties: Record<string, unknown> = {},
): void {
  if (!rollbarReady()) return;

  captureRollbarEvent(`product.${name}`, {
    product_event: name,
    properties,
  });
}

export function trackSignupCompleted(): void {
  if (!hasOptionalStorageConsent()) return;
  recordSignup();
  trackEvent("signup_completed", { method: "email" });
}

export function trackLoginCompleted(): void {
  trackEvent("login_completed", { method: "email" });
}

export function trackActiveSession(): void {
  if (!hasOptionalStorageConsent()) return;

  const session = recordActiveSession();
  const ready = rollbarReady();

  trackEvent("active_session", {
    active_date: session.activeDate,
    days_since_first_open: session.daysSinceFirstOpen,
  });

  if (ready && session.shouldReportD1) {
    trackEvent("activation_d1", {
      active_date: session.activeDate,
      days_since_first_open: session.daysSinceFirstOpen,
    });
    markD1Reported();
  }

  if (ready && session.shouldReportD7Candidate) {
    trackEvent("retention_d7_candidate", {
      active_date: session.activeDate,
      days_since_first_open: session.daysSinceFirstOpen,
    });
    markD7CandidateReported();
  }
}

export function trackOnboardingCompleted(): void {
  trackEvent("onboarding_completed", timeToValuePayload());
}

export function trackOnboardingDeferred(): void {
  trackEvent("onboarding_deferred", timeToValuePayload());
}

export function trackFirstDayMarked(props: {
  dayType: string;
  source: string;
}): void {
  trackEvent("first_day_marked", {
    day_type: props.dayType,
    source: props.source,
    ...timeToValuePayload(),
  });
}

export function trackDayMarked(props: {
  dayType: string;
  count: number;
  source: string;
}): void {
  trackEvent("day_marked", {
    day_type: props.dayType,
    count: props.count,
    source: props.source,
  });
}

export function trackCriticalFlowCompleted(props: {
  flow: string;
  durationMs?: number | null;
  metadata?: Record<string, unknown>;
}): void {
  trackEvent("critical_flow_completed", {
    flow: props.flow,
    duration_ms: props.durationMs ?? null,
    ...(props.metadata ?? {}),
  });
}

export function trackSupportFeedbackSent(props: {
  sentiment: "up" | "down";
  messageLength: number;
}): void {
  trackEvent("support_feedback_sent", {
    sentiment: props.sentiment,
    message_length: props.messageLength,
  });
}
